import { AuthAgent } from '@leapdev/auth-agent';
import { card } from 'api/card';
import { person } from 'api/person';
import { staff } from 'api/staff';
import { compare, validate } from 'compare-versions';
import { v4 as uuidv4 } from 'uuid';
import { Api, HostInfo, HostResponse, LeapContext, RefreshTokenResponse, RequestContext } from '../api';
import { accounting } from '../api/accounting';
import { app } from '../api/app';
import { calc } from '../api/calc';
import { communication } from '../api/communication';
import { document } from '../api/document';
import { infotrack } from '../api/infotrack';
import { lawconnect } from '../api/lawconnect';
import { matter } from '../api/matter';
import { msteams } from '../api/msteams';
import { register } from '../api/register';
import { schema } from '../api/schema';
import { system } from '../api/system';
import { config } from './config';
import { decodeToken, getQueryStringValue } from './util';

export abstract class BaseApi implements Api {
  abstract get accounting(): accounting.Api;
  abstract get app(): app.Api;
  abstract get calc(): calc.Api;
  abstract get card(): card.Api;
  abstract get communication(): communication.Api;
  abstract get document(): document.Api;
  abstract get infotrack(): infotrack.Api;
  abstract get lawconnect(): lawconnect.Api;
  abstract get staff(): staff.Api;
  abstract get msteams(): msteams.Api;
  abstract get person(): person.Api;
  abstract get register(): register.Api;
  abstract get schema(): schema.Api;
  abstract get matter(): matter.Api;
  abstract get system(): system.Api;

  protected _leapContext: LeapContext;
  protected _isTrustedApp: boolean;

  private _registeredCommands: { [command: string]: (params?: any) => void };
  private _clientId: string;
  private _passiveSystemCommands: string[] = ['onBeforeClose', 'onContextUpdate'];
  private _defaultLocale: string = 'en-AU';
  private _refreshTokenCache: RefreshTokenResponse;

  public returnRejectOnPermissionCancel: boolean;

  constructor(requestContext: RequestContext) {
    this._registeredCommands = {};

    const hostInfo = new HostInfo(
      requestContext.appSessionId,
      requestContext.host,
      null,
      requestContext.env,
      requestContext.region,
      this._defaultLocale
    );
    this._leapContext = new LeapContext(undefined, hostInfo, undefined, undefined);
    this._isTrustedApp = !!requestContext.trustedApp;
  }

  get leapContext(): LeapContext {
    return this._leapContext;
  }

  public get isTrustedApp(): boolean {
    return this._isTrustedApp;
  }

  private processNewLeapContext(
    leapContext: leapContextInternal,
    customLeapAuthClientId?: string
  ): {
    hasFinishedInit: boolean;
    leapContext?: LeapContext;
    contextError?: string;
  } {
    if (!leapContext) {
      return { hasFinishedInit: true, contextError: 'Invalid context from the host.', leapContext: null };
    }

    let context = leapContext.context;
    if (!context || (Object.keys(context).length === 0 && !!leapContext.matterContext)) {
      context = leapContext.matterContext;
    }

    if (leapContext.refresh_token_for_backend) {
      this._refreshTokenCache = {
        refreshToken: leapContext.refresh_token_for_backend,
        codeVerifier: null,
      };
    }

    this._clientId = leapContext.clientId;
    this._isTrustedApp = leapContext.isTrustedApp;

    const firmId = leapContext.firmId;
    const userId = leapContext.userId;
    const locale = leapContext.hostInfo.locale ?? this._defaultLocale;

    const hostInfo = new HostInfo(
      this.leapContext.hostInfo.appSessionId,
      leapContext.hostInfo.hostType,
      leapContext.hostInfo.hostVersion,
      leapContext.hostInfo.env,
      leapContext.hostInfo.region,
      locale,
      leapContext.hostInfo.accessibility
    );

    if (this.isTrustedApp) {
      if (customLeapAuthClientId) {
        // trusted apps with a custom clientId
        this._clientId = customLeapAuthClientId;
      } else if (!this._clientId) {
        // trusted apps without a clientId
        this._leapContext = new LeapContext(context, hostInfo, firmId, userId);
        this.hostRelatedSetup();
        return { hasFinishedInit: true, leapContext: this._leapContext };
      }
    } else if (!this._clientId) {
      // non-trusted apps without clientId
      return {
        hasFinishedInit: true,
        contextError: 'Failed to load the clientId from the schema',
        leapContext: null,
      };
    }

    this._leapContext = new LeapContext(context, hostInfo, firmId, userId);
    this.hostRelatedSetup();

    return { hasFinishedInit: false, leapContext: this._leapContext };
  }

  public init(leapAuthClientId?: string): Promise<boolean> {
    //close the window if the user doesn't accept the permissions
    const errMsg = getQueryStringValue('error');
    if (errMsg === 'access_denied') {
      if (this.returnRejectOnPermissionCancel) return new Promise((_, reject) => reject(new Error('Access denied')));
      else this.system.close();
    }

    return new Promise((resolve, reject) => {
      this.registerHostMessageHandler(
        this.leapContext.hostInfo.appSessionId,
        ({ data: leapContext }: leapContextResponse) => {
          this.deregisterHostMessageHandler(this.leapContext.hostInfo.appSessionId);

          const { hasFinishedInit, contextError } = this.processNewLeapContext(leapContext, leapAuthClientId);

          if (contextError) {
            console.error(contextError);
            return reject(false);
          }

          this.subscribeContextUpdate();

          // Attach the keep-screen-active activity listener once (no-op on desktop).
          this.addMouseMoveListener();

          if (hasFinishedInit) {
            return resolve(true);
          }

          const hostInfo = this.leapContext.hostInfo;
          const authEnv = hostInfo.env.includes('live') ? 'live' : hostInfo.env;
          const authHost = config.auth.host[authEnv][hostInfo.region];

          AuthAgent.init({
            authHost: authHost,
            clientId: this._clientId,
            autoLogin: true,
            autoLogout: false,
            uniqueSession: false,
            scopes: ['*'], //AUTH-218, defaults to defaultScopes for marketplace apps
            autoRefreshToken: false,
          })
            .then((isSuccess: string | boolean) => {
              if (!isSuccess) {
                console.error('Failed to init the auth-agent.');
                return false;
              }
              return AuthAgent.getAccessToken();
            })
            .then((token: string | false) => {
              if (!token) {
                console.error('Failed to get auth token.');
                return resolve(false);
              }
              return resolve(true);
            });
        }
      );

      this.retrieveContext();
    });
  }

  public subscribeContextUpdate(onSuccess?: (data: any) => void, onError?: (error: any) => void): void {
    this.deregisterHostMessageHandler('onContextUpdate');
    this.registerHostMessageHandler('onContextUpdate', (hostResponse: HostResponse) => {
      if (!hostResponse.isSuccess) {
        onError?.('Failed to retrieve the context update');
        return;
      }

      const { leapContext, contextError } = this.processNewLeapContext(hostResponse.data);
      if (contextError) onError?.(contextError);
      else onSuccess?.(leapContext);
    });
  }

  public unsubscribeContextUpdate(): void {
    this.deregisterHostMessageHandler('onContextUpdate');
  }

  public async getRefreshedAccessToken(): Promise<string> {
    if (this.leapContext.hostInfo.hostType === 'desktop') {
      const currentVersionNumber = this.leapContext.hostInfo.hostVersion;
      if (
        validate(currentVersionNumber) &&
        // Only LEAP Desktop version < '2.6.106.0' accepts command `internal.getAccessToken`
        compare(currentVersionNumber, '2.6.106.0', '<') &&
        this.isTrustedApp &&
        !this._clientId
      ) {
        return this.system.send({ command: 'internal.getAccessToken' }, true);
      }
    }
    return AuthAgent.getRefreshedAccessToken();
  }

  public async getDecodedRefreshedAccessToken(): Promise<any> {
    const accessToken = await this.getRefreshedAccessToken();
    return decodeToken(accessToken);
  }

  public async getRefreshToken(): Promise<RefreshTokenResponse> {
    const hostInfo = this.leapContext.hostInfo;

    // Check if we have a valid cached refresh token
    if (this._refreshTokenCache) {
      return this._refreshTokenCache;
    }

    if (hostInfo.hostType === 'desktop') {
      this._refreshTokenCache = await this.fetchRefreshTokenFromLeapAuth();
    } else if (hostInfo.hostType === 'web') {
      // For web hosts, the refresh token was already set in the cache during initialization
      // Just return the existing cache
      return this._refreshTokenCache;
    }

    return this._refreshTokenCache;
  }

  private async fetchRefreshTokenFromLeapAuth(): Promise<RefreshTokenResponse> {
    if (!this._clientId) {
      console.error('Failed to load the clientId from the schema');
      return null;
    }

    const codeVerifier = await this.internalSendAsync({
      command: 'internal.getCodeVerifier',
      request: { clientId: this._clientId },
    });

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      code: codeVerifier.code,
      redirect_uri: 'fourd://authcallback.com', // todo: need to support leap web
      client_id: this._clientId,
      code_verifier: codeVerifier.codeVerifier,
    });

    const authService = config.auth.refreshTokenService[this.leapContext.hostInfo.env];
    const response = await fetch(authService, {
      method: 'POST',
      headers: {
        'User-Agent': `HSDK_4.0_GetRefresh_${this._clientId}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });

    if (!response.ok) {
      console.error(`Failed to get refresh token: ${response.statusText}`);
      return null;
    }

    const data = await response.json();
    const refreshTokenResponse: RefreshTokenResponse = {
      refreshToken: data.refresh_token,
      codeVerifier: codeVerifier.codeVerifier,
    };

    return refreshTokenResponse;
  }

  protected abstract retrieveContext(): void;
  protected abstract hostRelatedSetup(): void;
  protected abstract addMouseMoveListener(): void;
  public abstract internalSendAsync(request: any): Promise<any>;
  public abstract internalSend(request: any): void;

  public registerHostMessageHandler(command: string, callback?: (param?: any) => void) {
    const alreadyRegistered = !!this._registeredCommands[command];

    if (!alreadyRegistered) {
      this._registeredCommands[command] = callback;
    } else {
      throw new Error('Command already registered');
    }
  }

  public deregisterHostMessageHandler(command: string) {
    delete this._registeredCommands[command];
  }

  /**
   * A helper function that makes 2-way call easier. It takes care of registering and deregistering message handler for you.
   * @param request the actual request object from LEAP app
   * @param requestFn a function specifying how a request object should be sent to the host system. It passes the requestId as parameter so that the request function can attach it to the request object.
   * @param responseFn a function specifying how a raw response from the host system should be handled / transformed
   * @return a promise of the final response
   */
  public twoWayCall<I, O>(
    request: I,
    requestFn: (requestId: string, request: I) => void,
    responseFn: (response: any) => O
  ): Promise<O> {
    const requestId = uuidv4();
    return new Promise((resolve, reject) => {
      this.registerHostMessageHandler(requestId, (hostResponse: HostResponse) => {
        this.deregisterHostMessageHandler(requestId);
        if (hostResponse.isSuccess) {
          resolve(responseFn(hostResponse.data));
        } else {
          reject(new Error(hostResponse.error));
        }
      });
      requestFn(requestId, request);
    });
  }

  protected handleCommand(command: string, payload?: string) {
    const registeredCommand = this._registeredCommands[command];

    if (!!registeredCommand && !!payload) {
      const params = JSON.parse(payload);
      registeredCommand(params);
    } else if (!!registeredCommand) {
      registeredCommand();
    } else if (!registeredCommand && !this._passiveSystemCommands.includes(command)) {
      throw new Error('Host command not registered');
    }
  }
}

interface leapContextResponse {
  isSuccess: boolean;
  data?: leapContextInternal;
}

interface leapContextInternal {
  apiKey: string;
  clientId: string;
  context: any;
  firmId: string;
  hostInfo: HostInfo;
  isTrustedApp: boolean;
  matterContext: any; //deprecated, leave here for backwards compatible
  refresh_token_for_backend: string;
  userId: string;
}
